import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ethers } from "ethers";
import { useAccount, useWalletClient } from "wagmi";
import uniswapRouterABI from "./UniswapV3RouterABI.json";
import poolABI from "./UniswapPoolABI.json";
import contractABI from "./AppleTree.json";
import {
  APTESG_ADDRESS,
  APTESG_POOL_ADDRESS,
  ARBITRUM_CHAIN_ID,
  UNISWAP_V3_FEE,
  UNISWAP_V3_QUOTER,
  UNISWAP_V3_ROUTER,
  WETH_ADDRESS,
  ensureArbitrumNetwork,
  getArbitrumReadProvider,
  getFriendlyWalletError,
} from "../config/arbitrum";

const QUOTER_ABI = [
  "function quoteExactInputSingle(address tokenIn, address tokenOut, uint24 fee, uint256 amountIn, uint160 sqrtPriceLimitX96) returns (uint256 amountOut)",
];

const WETH_ABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
];

const SLIPPAGE_BPS = 100;
const BPS_DENOMINATOR = 10000;
const BALANCE_FRESH_MS = 45000;
const POOL_FRESH_MS = 45000;
const FX_FRESH_MS = 130000;

const formatAmount = (value, maximumFractionDigits = 6) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "—";

  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits,
    minimumFractionDigits: numericValue > 0 && numericValue < 0.000001 ? maximumFractionDigits : 0,
  }).format(numericValue);
};

const formatUSD = (value) => {
  if (!Number.isFinite(value)) return "Unavailable";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const applySlippage = (amount) => amount
  .mul(BPS_DENOMINATOR - SLIPPAGE_BPS)
  .div(BPS_DENOMINATOR);

const parsePositiveAmount = (value) => {
  if (!value || !/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(value)) {
    throw new Error("Enter a valid amount.");
  }

  const fraction = value.split(".")[1] || "";
  if (fraction.length > 18) throw new Error("Use no more than 18 decimal places.");

  const parsed = ethers.utils.parseUnits(value, 18);
  if (parsed.lte(0)) throw new Error("Enter an amount greater than zero.");
  return parsed;
};

const normalizeDecimalInput = (value) => value.trim().replace(/,/g, ".");

const waitForConfirmedReceipt = async (transaction) => {
  try {
    return await transaction.wait(1);
  } catch (error) {
    if (error?.code === "TRANSACTION_REPLACED" && !error.cancelled) {
      if (error.receipt?.status === 1) return error.receipt;
      if (error.replacement) return error.replacement.wait(1);
    }
    throw error;
  }
};

const calculatePoolPrice = (sqrtPriceX96, token0, token1) => {
  const sqrtRatio = Number(sqrtPriceX96.toString()) / (2 ** 96);
  const token1PerToken0 = sqrtRatio ** 2;
  const normalizedToken0 = token0.toLowerCase();
  const normalizedToken1 = token1.toLowerCase();
  const normalizedAPTESG = APTESG_ADDRESS.toLowerCase();

  if (normalizedToken0 === normalizedAPTESG) return token1PerToken0;
  if (normalizedToken1 === normalizedAPTESG) return 1 / token1PerToken0;
  throw new Error("The configured pool does not contain APTESG.");
};

const getSwapQuote = async (amountIn, tokenIn, tokenOut) => {
  const quoter = new ethers.Contract(
    UNISWAP_V3_QUOTER,
    QUOTER_ABI,
    getArbitrumReadProvider()
  );

  return quoter.callStatic.quoteExactInputSingle(
    tokenIn,
    tokenOut,
    UNISWAP_V3_FEE,
    amountIn,
    0
  );
};

const getPoolPriceSnapshot = async () => {
  const pool = new ethers.Contract(
    APTESG_POOL_ADDRESS,
    poolABI,
    getArbitrumReadProvider()
  );
  const [slot0, token0, token1] = await Promise.all([
    pool.slot0(),
    pool.token0(),
    pool.token1(),
  ]);

  return calculatePoolPrice(slot0.sqrtPriceX96 || slot0[0], token0, token1);
};

const calculatePriceImpact = (input, output, direction, tokenPrice) => {
  const numericInput = Number(input);
  const numericOutput = Number(output);
  const numericTokenPrice = Number(tokenPrice);
  if (![numericInput, numericOutput, numericTokenPrice].every(Number.isFinite)
    || numericInput <= 0 || numericOutput <= 0 || numericTokenPrice <= 0) {
    return null;
  }

  const spotOutput = direction === "buy"
    ? numericInput / numericTokenPrice
    : numericInput * numericTokenPrice;
  if (!Number.isFinite(spotOutput) || spotOutput <= 0) return null;
  return Math.max(0, (1 - (numericOutput / spotOutput)) * 100);
};

export default function BuyAPTESG({ address, isConnected }) {
  const [mode, setMode] = useState("buy");
  const [payAsset, setPayAsset] = useState("ETH");
  const [amount, setAmount] = useState("");
  const [balanceData, setBalanceData] = useState({
    owner: null,
    value: null,
    status: "idle",
    updatedAt: null,
  });
  const [ethToUSD, setEthToUSD] = useState(null);
  const [ethUSDUpdatedAt, setEthUSDUpdatedAt] = useState(null);
  const [ethUSDSyncState, setEthUSDSyncState] = useState("loading");
  const [liveTokenPrice, setLiveTokenPrice] = useState(null);
  const [poolUpdatedAt, setPoolUpdatedAt] = useState(null);
  const [poolSyncState, setPoolSyncState] = useState("loading");
  const [quoteRaw, setQuoteRaw] = useState(null);
  const [executionMinimumRaw, setExecutionMinimumRaw] = useState(null);
  const [inputError, setInputError] = useState(null);
  const [isQuoting, setIsQuoting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [syncClock, setSyncClock] = useState(() => Date.now());
  const quoteRequestRef = useRef(0);
  const quoteContextRef = useRef(null);
  const balanceRequestRef = useRef(0);
  const poolRequestRef = useRef(0);
  const activeAddressRef = useRef(address);
  activeAddressRef.current = address;
  const { connector, chainId } = useAccount();
  const { data: walletClient } = useWalletClient();

  const refreshBalance = useCallback(async () => {
    const requestId = ++balanceRequestRef.current;
    const requestedAddress = address;

    if (!isConnected || !requestedAddress) {
      setBalanceData({ owner: null, value: null, status: "idle", updatedAt: null });
      return;
    }

    setBalanceData((current) => (
      current.owner?.toLowerCase() === requestedAddress.toLowerCase()
        ? current
        : { owner: requestedAddress, value: null, status: "loading", updatedAt: null }
    ));

    try {
      const token = new ethers.Contract(
        APTESG_ADDRESS,
        contractABI,
        getArbitrumReadProvider()
      );
      const rawBalance = await token.balanceOf(requestedAddress);
      if (requestId !== balanceRequestRef.current
        || activeAddressRef.current?.toLowerCase() !== requestedAddress.toLowerCase()) return;
      setBalanceData({
        owner: requestedAddress,
        value: ethers.utils.formatUnits(rawBalance, 18),
        status: "live",
        updatedAt: new Date(),
      });
    } catch (error) {
      console.error("Unable to refresh APTESG balance", error);
      if (requestId === balanceRequestRef.current
        && activeAddressRef.current?.toLowerCase() === requestedAddress.toLowerCase()) {
        setBalanceData({ owner: requestedAddress, value: null, status: "error", updatedAt: null });
      }
    }
  }, [address, isConnected]);

  const refreshPoolPrice = useCallback(async () => {
    const requestId = ++poolRequestRef.current;
    try {
      const nextPrice = await getPoolPriceSnapshot();
      if (requestId !== poolRequestRef.current) return;
      if (!Number.isFinite(nextPrice) || nextPrice <= 0) {
        throw new Error("The pool returned an invalid price.");
      }
      setLiveTokenPrice(nextPrice);
      setPoolUpdatedAt(new Date());
      setPoolSyncState("live");
    } catch (error) {
      console.error("Unable to refresh the APTESG pool price", error);
      if (requestId === poolRequestRef.current) {
        setLiveTokenPrice(null);
        setPoolUpdatedAt(null);
        setPoolSyncState("error");
      }
    }
  }, []);

  useEffect(() => {
    let active = true;

    const refreshETHUSD = async () => {
      try {
        const response = await fetch(
          "https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd"
        );
        if (!response.ok) throw new Error(`ETH/USD request failed (${response.status})`);
        const data = await response.json();
        const nextPrice = Number(data?.ethereum?.usd);
        if (!Number.isFinite(nextPrice) || nextPrice <= 0) {
          throw new Error("ETH/USD response did not include a valid price.");
        }
        if (active) {
          setEthToUSD(nextPrice);
          setEthUSDUpdatedAt(new Date());
          setEthUSDSyncState("live");
        }
      } catch (error) {
        console.error("Unable to refresh ETH/USD", error);
        if (active) {
          setEthToUSD(null);
          setEthUSDUpdatedAt(null);
          setEthUSDSyncState("error");
        }
      }
    };

    refreshETHUSD();
    const interval = window.setInterval(refreshETHUSD, 60000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => setSyncClock(Date.now()), 10000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    refreshBalance();
    if (!isConnected || !address) return undefined;

    const token = new ethers.Contract(
      APTESG_ADDRESS,
      contractABI,
      getArbitrumReadProvider()
    );
    const incoming = token.filters.Transfer(null, address);
    const outgoing = token.filters.Transfer(address, null);
    const handleTransfer = () => refreshBalance();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refreshBalance();
    };

    token.on(incoming, handleTransfer);
    token.on(outgoing, handleTransfer);
    window.addEventListener("focus", refreshBalance);
    document.addEventListener("visibilitychange", handleVisibility);
    const interval = window.setInterval(refreshBalance, 15000);

    return () => {
      token.off(incoming, handleTransfer);
      token.off(outgoing, handleTransfer);
      window.removeEventListener("focus", refreshBalance);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.clearInterval(interval);
    };
  }, [address, isConnected, refreshBalance]);

  useEffect(() => {
    refreshPoolPrice();
    const pool = new ethers.Contract(
      APTESG_POOL_ADDRESS,
      poolABI,
      getArbitrumReadProvider()
    );
    const handleSwap = () => refreshPoolPrice();
    pool.on("Swap", handleSwap);
    const interval = window.setInterval(refreshPoolPrice, 15000);

    return () => {
      pool.off("Swap", handleSwap);
      window.clearInterval(interval);
    };
  }, [refreshPoolPrice]);

  useEffect(() => {
    const requestId = ++quoteRequestRef.current;
    if (isSubmitting) return undefined;

    quoteContextRef.current = null;
    setQuoteRaw(null);
    setInputError(null);

    if (!amount) {
      setIsQuoting(false);
      return undefined;
    }

    let amountIn;
    try {
      amountIn = parsePositiveAmount(amount);
    } catch (error) {
      setIsQuoting(false);
      setInputError(error.message);
      return undefined;
    }

    setIsQuoting(true);
    const timer = window.setTimeout(async () => {
      try {
        const tokenIn = mode === "buy" ? WETH_ADDRESS : APTESG_ADDRESS;
        const tokenOut = mode === "buy" ? APTESG_ADDRESS : WETH_ADDRESS;
        const quote = await getSwapQuote(amountIn, tokenIn, tokenOut);
        if (requestId === quoteRequestRef.current) {
          quoteContextRef.current = { amount, mode, raw: quote };
          setQuoteRaw(quote);
        }
      } catch (error) {
        if (requestId === quoteRequestRef.current) {
          console.error("Unable to quote APTESG swap", error);
          setStatus({ type: "error", text: "No executable quote is available for this amount." });
        }
      } finally {
        if (requestId === quoteRequestRef.current) setIsQuoting(false);
      }
    }, 350);

    return () => window.clearTimeout(timer);
  }, [amount, isSubmitting, mode, liveTokenPrice]);

  const hasCurrentBalance = Boolean(
    address
    && balanceData.owner
    && balanceData.owner.toLowerCase() === address.toLowerCase()
  );
  const aptesgBalance = hasCurrentBalance ? balanceData.value : null;
  const balanceUpdatedAt = hasCurrentBalance ? balanceData.updatedAt : null;
  const balanceSyncState = hasCurrentBalance
    ? balanceData.status
    : (isConnected ? "loading" : "idle");

  const aptesgUSD = useMemo(() => {
    if (aptesgBalance === null || liveTokenPrice === null || ethToUSD === null) {
      return "Unavailable";
    }
    const balance = Number(aptesgBalance);
    const aptesgETH = Number(liveTokenPrice);
    const ethUSD = Number(ethToUSD);
    if (![balance, aptesgETH, ethUSD].every(Number.isFinite) || aptesgETH <= 0 || ethUSD <= 0) {
      return "Unavailable";
    }
    return formatUSD(balance * aptesgETH * ethUSD);
  }, [aptesgBalance, ethToUSD, liveTokenPrice]);

  const syncIndicator = useMemo(() => {
    const isRecent = (updatedAt, maxAge) => (
      updatedAt instanceof Date && syncClock - updatedAt.getTime() <= maxAge
    );
    const poolIsFresh = poolSyncState === "live" && isRecent(poolUpdatedAt, POOL_FRESH_MS);
    const fxIsFresh = ethUSDSyncState === "live" && isRecent(ethUSDUpdatedAt, FX_FRESH_MS);
    const balanceIsFresh = !isConnected || (
      balanceSyncState === "live" && isRecent(balanceUpdatedAt, BALANCE_FRESH_MS)
    );

    if (poolIsFresh && fxIsFresh && balanceIsFresh) return { state: "live", label: "Live" };
    if (poolSyncState === "error" || ethUSDSyncState === "error" || balanceSyncState === "error") {
      return { state: "error", label: "Sync issue" };
    }
    return { state: "syncing", label: "Syncing" };
  }, [
    balanceSyncState,
    balanceUpdatedAt,
    ethUSDSyncState,
    ethUSDUpdatedAt,
    isConnected,
    poolSyncState,
    poolUpdatedAt,
    syncClock,
  ]);

  const outputAmount = quoteRaw ? ethers.utils.formatUnits(quoteRaw, 18) : null;
  const visibleMinimumRaw = isSubmitting && executionMinimumRaw
    ? executionMinimumRaw
    : (quoteRaw ? applySlippage(quoteRaw) : null);
  const minimumReceived = visibleMinimumRaw
    ? ethers.utils.formatUnits(visibleMinimumRaw, 18)
    : null;
  const outputSymbol = mode === "buy" ? "APTESG" : "ETH";
  const inputSymbol = mode === "buy" ? payAsset : "APTESG";

  const priceImpact = useMemo(() => {
    if (!outputAmount || !liveTokenPrice || !amount) return null;
    return calculatePriceImpact(amount, outputAmount, mode, liveTokenPrice);
  }, [amount, liveTokenPrice, mode, outputAmount]);

  const updateAmount = (nextAmount) => {
    quoteContextRef.current = null;
    setQuoteRaw(null);
    setInputError(null);
    setStatus(null);
    setAmount(nextAmount);
  };

  const changeMode = (nextMode) => {
    setMode(nextMode);
    setAmount("");
    quoteContextRef.current = null;
    setQuoteRaw(null);
    setInputError(null);
    setStatus(null);
  };

  const executeSwap = async () => {
    if (!isConnected || !address) {
      setStatus({ type: "error", text: "Connect your wallet before trading." });
      return;
    }

    let amountIn;
    try {
      amountIn = parsePositiveAmount(amount);
    } catch (error) {
      setInputError(error.message);
      setStatus({ type: "error", text: error.message });
      return;
    }

    const quoteAtClick = quoteContextRef.current;
    if (!quoteRaw
      || !quoteAtClick
      || quoteAtClick.amount !== amount
      || quoteAtClick.mode !== mode
      || !quoteAtClick.raw.eq(quoteRaw)) {
      setStatus({ type: "error", text: "The quote changed. Wait for the latest quote and try again." });
      return;
    }
    const userApprovedMinimum = applySlippage(quoteAtClick.raw);
    if (userApprovedMinimum.lte(0)) {
      setStatus({ type: "error", text: "This amount is too small to trade safely." });
      return;
    }

    setIsSubmitting(true);
    setExecutionMinimumRaw(userApprovedMinimum);
    setStatus({ type: "pending", text: "Requesting Arbitrum One in your wallet…" });

    try {
      const eip1193 = await ensureArbitrumNetwork(walletClient, connector);
      const provider = new ethers.providers.Web3Provider(eip1193, "any");
      const network = await provider.getNetwork();
      if (network.chainId !== ARBITRUM_CHAIN_ID) {
        throw new Error("The wallet is not connected to Arbitrum One.");
      }

      const expectedAddress = ethers.utils.getAddress(address);
      const signer = provider.getSigner(expectedAddress);
      const signerAddress = await signer.getAddress();
      const assertWalletState = async () => {
        const [activeChainId, activeAccounts] = await Promise.all([
          eip1193.request({ method: "eth_chainId" }),
          eip1193.request({ method: "eth_accounts" }),
        ]);
        const parsedChainId = typeof activeChainId === "string"
          ? Number.parseInt(activeChainId, 16)
          : Number(activeChainId);
        const currentUIAddress = activeAddressRef.current;

        if (parsedChainId !== ARBITRUM_CHAIN_ID) {
          throw new Error("The wallet left Arbitrum One. Switch back and retry.");
        }
        if (!currentUIAddress
          || !Array.isArray(activeAccounts)
          || !activeAccounts[0]
          || ethers.utils.getAddress(activeAccounts[0]) !== expectedAddress
          || ethers.utils.getAddress(currentUIAddress) !== expectedAddress) {
          throw new Error("The active wallet account changed. Review the new balance and retry the trade.");
        }

        return expectedAddress;
      };

      if (ethers.utils.getAddress(signerAddress) !== expectedAddress) {
        throw new Error("The active wallet account changed. Review the new balance and retry the trade.");
      }

      const router = new ethers.Contract(UNISWAP_V3_ROUTER, uniswapRouterABI, signer);
      let transaction;

      if (mode === "buy") {
        if (payAsset === "ETH") {
          const ethBalance = await provider.getBalance(signerAddress);
          if (ethBalance.lt(amountIn)) throw new Error("Not enough ETH for this purchase.");
        } else {
          const weth = new ethers.Contract(WETH_ADDRESS, WETH_ABI, signer);
          const [wethBalance, allowance] = await Promise.all([
            weth.balanceOf(signerAddress),
            weth.allowance(signerAddress, UNISWAP_V3_ROUTER),
          ]);
          if (wethBalance.lt(amountIn)) throw new Error("Not enough WETH for this purchase.");

          if (allowance.lt(amountIn)) {
            await assertWalletState();
            setStatus({ type: "pending", text: "Approve WETH in your wallet (step 1 of 2)…" });
            const approval = await weth.approve(UNISWAP_V3_ROUTER, amountIn);
            await waitForConfirmedReceipt(approval);
            await assertWalletState();
          }
        }
      } else {
        const token = new ethers.Contract(APTESG_ADDRESS, contractABI, signer);
        const [balance, allowance] = await Promise.all([
          token.balanceOf(signerAddress),
          token.allowance(signerAddress, UNISWAP_V3_ROUTER),
        ]);
        if (balance.lt(amountIn)) throw new Error("Not enough APTESG for this sale.");

        if (allowance.lt(amountIn)) {
          await assertWalletState();
          setStatus({ type: "pending", text: "Approve APTESG in your wallet (step 1 of 2)…" });
          const approval = await token.approve(UNISWAP_V3_ROUTER, amountIn);
          await waitForConfirmedReceipt(approval);
          await assertWalletState();
        }
      }

      await assertWalletState();
      const [freshQuote, freshPoolPrice] = await Promise.all([
        getSwapQuote(
          amountIn,
          mode === "buy" ? WETH_ADDRESS : APTESG_ADDRESS,
          mode === "buy" ? APTESG_ADDRESS : WETH_ADDRESS
        ),
        getPoolPriceSnapshot(),
      ]);
      if (!freshQuote || freshQuote.lte(0)) throw new Error("The latest quote cannot be executed.");

      const freshImpact = calculatePriceImpact(
        ethers.utils.formatUnits(amountIn, 18),
        ethers.utils.formatUnits(freshQuote, 18),
        mode,
        freshPoolPrice
      );
      if (freshImpact === null) {
        throw new Error("The latest price impact could not be verified. Retry when live pricing is available.");
      }
      const freshMinimum = applySlippage(freshQuote);
      if (freshMinimum.lte(0)) {
        throw new Error("This amount is too small to trade safely.");
      }

      poolRequestRef.current += 1;
      quoteContextRef.current = { amount, mode, raw: freshQuote };
      setQuoteRaw(freshQuote);
      setLiveTokenPrice(freshPoolPrice);
      setPoolUpdatedAt(new Date());
      setPoolSyncState("live");

      if (freshQuote.lt(userApprovedMinimum)) {
        setStatus({
          type: "warning",
          text: "The price moved beyond your 1% limit. Review the updated quote and press the trade button again.",
        });
        return;
      }

      if (freshImpact >= 10) {
        const confirmed = window.confirm(
          `The latest executable quote has an estimated ${freshImpact.toFixed(1)}% price impact because liquidity is limited. Continue?`
        );
        if (!confirmed) {
          setStatus({ type: "info", text: "Trade cancelled before the swap transaction." });
          return;
        }
      }

      await assertWalletState();
      const amountOutMinimum = userApprovedMinimum;
      const deadline = Math.floor(Date.now() / 1000) + 600;

      if (mode === "buy") {
        const params = {
          tokenIn: WETH_ADDRESS,
          tokenOut: APTESG_ADDRESS,
          fee: UNISWAP_V3_FEE,
          recipient: expectedAddress,
          deadline,
          amountIn,
          amountOutMinimum,
          sqrtPriceLimitX96: 0,
        };

        if (payAsset === "ETH") {
          const ethBalance = await provider.getBalance(expectedAddress);
          const gasEstimate = await router.estimateGas.exactInputSingle(params, { value: amountIn });
          const feeData = await provider.getFeeData();
          const gasPrice = feeData.maxFeePerGas || feeData.gasPrice || ethers.BigNumber.from(0);
          if (ethBalance.lt(amountIn.add(gasEstimate.mul(gasPrice)))) {
            throw new Error("Not enough ETH for the trade amount and network fee.");
          }
          await router.callStatic.exactInputSingle(params, { value: amountIn });
          await assertWalletState();
          setStatus({ type: "pending", text: "Confirm the APTESG purchase in your wallet…" });
          transaction = await router.exactInputSingle(params, { value: amountIn });
        } else {
          await router.callStatic.exactInputSingle(params);
          await assertWalletState();
          setStatus({ type: "pending", text: "Confirm the APTESG purchase in your wallet…" });
          transaction = await router.exactInputSingle(params);
        }
      } else {

        const params = {
          tokenIn: APTESG_ADDRESS,
          tokenOut: WETH_ADDRESS,
          fee: UNISWAP_V3_FEE,
          recipient: UNISWAP_V3_ROUTER,
          deadline,
          amountIn,
          amountOutMinimum,
          sqrtPriceLimitX96: 0,
        };
        const swapData = router.interface.encodeFunctionData("exactInputSingle", [params]);
        const unwrapData = router.interface.encodeFunctionData("unwrapWETH9", [
          amountOutMinimum,
          expectedAddress,
        ]);
        const calls = [swapData, unwrapData];

        await router.callStatic.multicall(calls);
        await assertWalletState();
        setStatus({ type: "pending", text: "Confirm the APTESG sale in your wallet…" });
        transaction = await router.multicall(calls);
      }

      setStatus({ type: "pending", text: "Transaction submitted. Waiting for confirmation…" });
      const receipt = await waitForConfirmedReceipt(transaction);
      await Promise.allSettled([refreshBalance(), refreshPoolPrice()]);
      window.setTimeout(refreshBalance, 1500);
      setAmount("");
      quoteContextRef.current = null;
      setQuoteRaw(null);
      setStatus({
        type: "success",
        text: mode === "buy" ? "APTESG purchase confirmed." : "APTESG sale confirmed; ETH was sent to your wallet.",
        hash: receipt.transactionHash,
      });
    } catch (error) {
      console.error("APTESG swap failed", error);
      setStatus({ type: "error", text: getFriendlyWalletError(error) });
    } finally {
      setExecutionMinimumRaw(null);
      setIsSubmitting(false);
    }
  };

  const quoteContext = quoteContextRef.current;
  const hasValidInput = Boolean(
    amount
    && quoteRaw
    && quoteContext
    && quoteContext.amount === amount
    && quoteContext.mode === mode
    && quoteContext.raw.eq(quoteRaw)
    && !isQuoting
  );
  const canSubmit = isConnected && address && hasValidInput && !isSubmitting;
  const actionLabel = chainId && chainId !== ARBITRUM_CHAIN_ID
    ? `Switch to Arbitrum & ${mode === "buy" ? "Buy" : "Sell"}`
    : `${mode === "buy" ? "Buy" : "Sell"} APTESG`;

  return (
    <section className="buy-token-section swap-card">
      <div className="swap-card-heading">
        <div>
          <p className="swap-eyebrow">Arbitrum One · Uniswap V3</p>
          <h2>Trade APTESG</h2>
        </div>
        <span
          className={`swap-live-indicator ${syncIndicator.state}`}
          title="Pool price, ETH/USD and connected-wallet balance sync status"
        >
          <span /> {syncIndicator.label}
        </span>
      </div>

      <div className="swap-mode-tabs" role="tablist" aria-label="Choose trade direction">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "buy"}
          className={mode === "buy" ? "active" : ""}
          onClick={() => changeMode("buy")}
          disabled={isSubmitting}
        >
          Buy APTESG
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "sell"}
          className={mode === "sell" ? "active" : ""}
          onClick={() => changeMode("sell")}
          disabled={isSubmitting}
        >
          Sell APTESG
        </button>
      </div>

      <div className="swap-balance-grid" aria-live="polite">
        <div>
          <span>Your Balance</span>
          <strong title={aptesgBalance !== null ? `${aptesgBalance} APTESG` : undefined}>
            {!isConnected
              ? "— APTESG"
              : balanceSyncState === "loading"
                ? "Syncing…"
                : aptesgBalance === null
                  ? "Unavailable"
                  : `${formatAmount(aptesgBalance)} APTESG`}
          </strong>
        </div>
        <div>
          <span>Estimated USD Value</span>
          <strong>{isConnected ? aptesgUSD : "—"}</strong>
        </div>
      </div>

      {mode === "buy" && (
        <div className="swap-pay-row">
          <span>Pay with</span>
          <div className="swap-asset-toggle" aria-label="Choose payment token">
            {["ETH", "WETH"].map((asset) => (
              <button
                type="button"
                key={asset}
                className={payAsset === asset ? "active" : ""}
                onClick={() => setPayAsset(asset)}
                disabled={isSubmitting}
              >
                {asset}
              </button>
            ))}
          </div>
        </div>
      )}

      <label className="swap-input-label" htmlFor="aptesg-swap-amount">
        <span>{mode === "buy" ? "You pay" : "You sell"}</span>
        <div className="swap-input-shell">
          <input
            id="aptesg-swap-amount"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            placeholder="0.0"
            value={amount}
            onChange={(event) => updateAmount(normalizeDecimalInput(event.target.value))}
            disabled={isSubmitting}
          />
          {mode === "sell" && isConnected && aptesgBalance !== null && (
            <button
              type="button"
              className="swap-max-button"
              onClick={() => updateAmount(aptesgBalance)}
              disabled={isSubmitting || balanceSyncState !== "live"}
            >
              MAX
            </button>
          )}
          <strong>{inputSymbol}</strong>
        </div>
      </label>
      {inputError && <p className="swap-input-error" role="alert">{inputError}</p>}

      <div className="swap-quote" aria-live="polite">
        <div>
          <span>You receive</span>
          <strong>{isQuoting ? "Quoting…" : outputAmount ? `≈ ${formatAmount(outputAmount, 8)} ${outputSymbol}` : "—"}</strong>
        </div>
        <div>
          <span>Minimum received (1% slippage)</span>
          <strong>{minimumReceived ? `${formatAmount(minimumReceived, 8)} ${outputSymbol}` : "—"}</strong>
        </div>
        {priceImpact !== null && (
          <div>
            <span>Estimated price impact</span>
            <strong className={priceImpact >= 5 ? "swap-impact-warning" : ""}>
              {priceImpact.toFixed(2)}%
            </strong>
          </div>
        )}
      </div>

      <button
        type="button"
        className="btn btn-main swap-submit-button"
        onClick={executeSwap}
        disabled={!canSubmit}
      >
        {isSubmitting ? "Processing…" : !isConnected ? "Connect wallet to trade" : actionLabel}
      </button>

      <p className="swap-liquidity-note">
        Quotes include pool fees and price impact. Market depth is limited, so review larger trades carefully.
      </p>

      {status && (
        <div className={`swap-status ${status.type}`} role={status.type === "error" ? "alert" : "status"}>
          <span>{status.text}</span>
          {status.hash && (
            <a
              href={`https://arbiscan.io/tx/${status.hash}`}
              target="_blank"
              rel="noreferrer"
            >
              View on Arbiscan
            </a>
          )}
        </div>
      )}

      <p className="swap-update-time">
        {!isConnected
          ? "Balance sync starts after wallet connection."
          : balanceSyncState === "error"
            ? "Balance unavailable; retrying automatically."
            : balanceUpdatedAt
              ? `Balance synced ${balanceUpdatedAt.toLocaleTimeString()}`
              : "Syncing your Arbitrum balance…"}
      </p>
    </section>
  );
}
